import { createContext } from '@/utils/react-utils';
import { Flex, HStack, Text } from '@chakra-ui/react';
import moment from 'moment';
import React from 'react';

import { useSelectedForm } from '../../hooks/useSelectedForm';
import { ItemQuestion, ItemSection } from '../../interfaces';
import { QuestionContent } from './QuestionContent/QuestionContent';

type QuestionContextProps = {
  paneOpen: 'actions' | 'attachments' | null;
  setQuestion: React.Dispatch<React.SetStateAction<ItemQuestion>>;
  togglePane: (pane: 'actions' | 'attachments') => void;
  question: ItemQuestion;
  section: ItemSection;
};

const [QuestionContext, useQuestionContext] = createContext<
  QuestionContextProps
>({
  name: 'QuestionContext',
});

const QuestionDetails: React.FC = () => {
  const { question, section } = useQuestionContext();
  const form = useSelectedForm();

  const firstAnswer = question.answers?.[0];

  return (
    <Flex w="full" flexDir="column" gap="2">
      <HStack alignItems="flex-start" justify="space-between">
        {!!firstAnswer && (
          <Text
            wordBreak="break-all"
            color="gray.400"
            fontWeight="bold"
            fontSize="sm"
          >
            {`${form?.formName} / ${section.name}${
              firstAnswer?.questionParentOptionName
                ? ` / ${firstAnswer?.questionParentOptionName}`
                : ''
            } / ${moment(firstAnswer.answeredAt).format('hh:mm a')}`}
          </Text>
        )}
      </HStack>

      <Text
        wordBreak={{ base: 'break-all', md: 'break-word' }}
        color="darkText"
        fontWeight="bold"
        fontSize="15px"
        lineHeight={1}
      >
        {question.question}
      </Text>
    </Flex>
  );
};

export const QuestionItemComponent: React.FC<{
  question: ItemQuestion;
  section: ItemSection;
}> = ({ question: _question, section }) => {
  const [question, setQuestion] = React.useState<ItemQuestion>(_question);
  const [paneOpen, setPaneOpen] = React.useState<
    'actions' | 'attachments' | null
  >(null);

  const togglePane = React.useCallback(
    (pane: 'actions' | 'attachments' | null) => {
      setPaneOpen((old) => (pane === old ? null : pane));
    },
    [],
  );

  return (
    <QuestionContext
      key={question.id}
      value={{
        paneOpen,
        setQuestion,
        togglePane,
        question,
        section,
      }}
    >
      <Flex
        overflow="clip"
        boxShadow="initial"
        flexDir="column"
        borderWidth="thin"
        w="full"
        rounded="xl"
        bg="white"
      >
        <Flex p="3.5" gap={4} flexDir="column">
          <QuestionDetails />
          <Flex w="full">
            <QuestionContent question={question} />
          </Flex>
        </Flex>
      </Flex>
    </QuestionContext>
  );
};

export const QuestionItem = React.memo(QuestionItemComponent);

export { useQuestionContext };
